import React, { useEffect, useState } from 'react';
import Loader from '../UI/Loader/Loader';
import { Formik, Form } from 'formik';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { PrimaryButton } from "../UI";
import Dropdown from '../UI/Dropdown/Dropdown';
import { getContentTypes } from '../../utils/APIs';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { useUserContext } from '../../contexts/UserContext';
import { compareJson, isValidJsonString } from '../../utils/helper';

const CreateContentfulModal = ({ closeModal, loading, handleSubmit, error, selectedTemplate, experienceJson }) => {
    const { content } = useContentfulContext();
    const { userData } = useUserContext();
    const [types, setTypes] = useState([]);
    const [fetchingTypes, setFetchingTypes] = useState(false);

    // Helper: a content type is a "single JSON field" type when it has exactly 1 field
    // of Contentful type "Object" (the JSON field type in Contentful's type system)
    const isSingleJsonFieldType = (type) =>
        type?.fields?.length === 1 && type.fields[0]?.type === 'Object';

    const dropdownItems = types.map(type => ({
        name: type.name,
        val: type.sys.id,
        // fieldKey is only set for single-JSON-field types — the caller wraps content under this key.
        // For multi-field types, fieldKey is null — the caller spreads content directly.
        fieldKey: isSingleJsonFieldType(type) ? type.fields[0].id : null,
        isSingleField: isSingleJsonFieldType(type),
    }));

    const fetchContentTypes = async () => {
        try {
            setFetchingTypes(true)
            const result = await getContentTypes({
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                contentful_id: userData?.defaultContentful?._id,
            });
            if (result.status) {
                let fetchedTypes = result.data.items || [];

                if (selectedTemplate && selectedTemplate.json_schema && isValidJsonString(selectedTemplate.json_schema)) {
                    // --- Template selected: match against template schema ---
                    const templateSchema = JSON.parse(selectedTemplate.json_schema);
                    const templateTopLevelKeys = Object.keys(templateSchema);
                    fetchedTypes = fetchedTypes.filter(type => {
                        const fields = type.fields || [];
                        const fieldIds = fields.map(f => f.id);

                        // Empty template schema only matches a content type with no fields
                        if (templateTopLevelKeys.length === 0) return fieldIds.length === 0;

                        // Exact multi-field match: same number of fields AND all template keys
                        // exist as field IDs (and vice-versa, since counts are equal)
                        if (
                            fieldIds.length === templateTopLevelKeys.length &&
                            templateTopLevelKeys.every(key => fieldIds.includes(key))
                        ) return true;

                        // Single-JSON-field match: content type has exactly 1 field of Contentful
                        // type "Object" (JSON). Its inner value would contain the template keys.
                        if (fields.length === 1 && fields[0]?.type === 'Object') return true;

                        return false;
                    });
                } else {
                    // --- No template selected: use experienceJson top-level keys for matching ---
                    const expKeys = experienceJson && typeof experienceJson === 'object'
                        ? Object.keys(experienceJson)
                        : [];

                    if (expKeys.length > 0) {
                        // Filter content types that are compatible with the experience JSON keys
                        const compatibleMatches = fetchedTypes.filter(type => {
                            const fields = type.fields || [];
                            const fieldIds = fields.map(f => f.id);

                            // 1. Every key in expKeys must exist as a field ID in the content type
                            const allKeysSupported = expKeys.every(key => fieldIds.includes(key));
                            if (!allKeysSupported) return false;

                            // 2. Every required field in the content type must exist in expKeys
                            const allRequiredFieldsPresent = fields.every(field => {
                                return !field.required || expKeys.includes(field.id);
                            });

                            return allRequiredFieldsPresent;
                        });

                        if (compatibleMatches.length > 0) {
                            fetchedTypes = compatibleMatches;
                        } else {
                            // No compatible multi-field matches — show only single JSON field types
                            fetchedTypes = fetchedTypes.filter(type => {
                                const fields = type.fields || [];
                                return fields.length === 1 && fields[0]?.type === 'Object';
                            });
                        }
                    } else {
                        // No experience JSON available — show only single JSON field types
                        fetchedTypes = fetchedTypes.filter(type => {
                            const fields = type.fields || [];
                            return fields.length === 1 && fields[0]?.type === 'Object';
                        });
                    }
                }

                setTypes(fetchedTypes);
            }
        } catch (err) {
            console.error('Error fetching content types:', err);
        } finally{
            setFetchingTypes(false)
        }
    };

    useEffect(() => {
        fetchContentTypes();
    }, []);

    return (
        <PrimaryModal title={'Create Contentful Entry'} hideModal={closeModal}>
            <div className="p-6 space-y-6">
                {loading && <Loader />}
                <Formik
                    initialValues={{ selected_type: '', selected_field_key: '', is_single_field: false }}
                    onSubmit={(values) => {
                        if (values.selected_type) {
                            handleSubmit({
                                id: values.selected_type,
                                // fieldKey is null for multi-field content types; caller handles accordingly
                                fieldKey: values.selected_field_key || null,
                                isSingleField: values.is_single_field,
                            });
                        }
                    }}
                >
                    {({ errors, touched, values, setFieldValue, setFieldTouched }) => (
                        <Form className="space-y-4" id="create-contentful-form">
                            <div>
                                <label htmlFor="selected_type" className="block text-sm font-medium text-gray-700 mb-1">
                                    {'Select Content Type to Create'}
                                </label>
                                {fetchingTypes ? (
                                    <div className="text-sm text-gray-500">getting content types...</div>
                                ) : types.length > 0 ? (
                                    <Dropdown
                                        items={dropdownItems}
                                        width="300"
                                        callback={(selected) => {
                                            setFieldValue('selected_type', selected?.val || '');
                                            setFieldValue('selected_field_key', selected?.fieldKey || '');
                                            setFieldValue('is_single_field', selected?.isSingleField || false);
                                            setFieldTouched('selected_type', true);
                                        }}
                                        noItemSelectedTitle={'Select Content Type'}
                                        searchable={false}
                                        multiSelect={false}
                                    />
                                ) : (
                                    <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm border border-red-200">
                                        No content type matching with experience json.
                                    </div>
                                )}
                                {errors.selected_type && touched.selected_type && (
                                    <p className="mt-2 text-sm text-red-500">{errors.selected_type}</p>
                                )}
                            </div>
                            {error && <p className="text-sm text-red-500">{error}</p>}
                            {types.length !== 0 &&<div className="flex justify-end">
                                <PrimaryButton type="submit" disabled={types.length === 0 || fetchingTypes}>
                                    {content.UPDATE_OFFERS_MODAL.submit}
                                </PrimaryButton>
                            </div>}
                        </Form>
                    )}
                </Formik>
            </div>
        </PrimaryModal>
    );
};

export default CreateContentfulModal;
